import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../../variables/constants";
import { getOrCreateDeviceId } from "../../variables/deviceId";
import { ToastContainer } from 'react-toastify';
import { Form, Input, Button, Card, Typography, Modal } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined, PhoneOutlined, EnvironmentOutlined, WhatsAppOutlined, MailOutlined } from '@ant-design/icons';
import entete from "../../assets/img/entete_pool.png";
import poolins from "../../assets/img/poolins.jpg";

const { Title, Text } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [insurances, setInsurances] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/api/organisme/all/assurances-actives`).then(response => {
      if (response.data) {
        setInsurances(Array.isArray(response.data) ? response.data : response.data?.insurances ?? []);
      }
    }).catch(error => {
      console.error("Erreur lors de la récupération des logos des assurances:", error);
    });
  }, []);

  const handleSubmit = (values) => {
    submitLogin({
      userName: values.userName,
      password: values.password,
      forceLogout: false,
    });
  };

  // Envoi de la connexion. `user.forceLogout` = true après confirmation de la modale « session déjà
  // active » : la connexion ferme alors l'autre session et prend la main.
  const submitLogin = (user) => {
    setLoading(true);
    // Sécurité par appareil : joindre le deviceId (créé/mémorisé en localStorage). Inconnu du
    // backend ⇒ challenge OTP + alerte e-mail.
    const payload = { ...user, deviceId: getOrCreateDeviceId() };
    axios
      .post(`${API_URL}/api/authenticate`, JSON.stringify(payload), {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      })
      .then((res) => {
        sessionStorage.setItem("t", res.data.split(".")[1]);
        sessionStorage.setItem("k", res.data.split(".")[2]);
        sessionStorage.setItem("n", res.data.split(".")[0]);

        axios
          .get(`${API_URL}/api/utilisateur/session/${user.userName}`, {
            headers: {
              Authorization: `Bearer ${res.data}`,
              "Content-Type": "application/json",
            },
          })
          .then((resp) => {
            if (!resp.data?.active) {
              toast.error("Votre compte n'est pas actif.");
              setLoading(false);
              return;
            }

            if (resp.data?.dateExpiration === null) {
              if (resp.data.active) {
                sessionStorage.setItem("user", JSON.stringify(resp.data));
                toast("Veuillez définir votre nouveau mot de passe", {
                  position: "top-center",
                  autoClose: 2000,
                  onClose: () => {
                    navigate("/first-connexion", {
                      state: {
                        username: resp.data.userName,
                        email: resp.data.email
                      }
                    });
                  }
                });
                return;
              } else {
                toast.error("Votre compte n'est pas actif.");
                return;
              }
            }

            const expirationDate = new Date(resp.data?.dateExpiration);
            const today = new Date();

            if (expirationDate && expirationDate <= today) {
              toast.info("Votre mot de passe est expiré. Veuillez en définir un nouveau");
              // On conserve l'utilisateur en session et on transmet l'email afin
              // qu'il soit prérempli sur l'écran de changement de mot de passe.
              sessionStorage.setItem("user", JSON.stringify(resp.data));
              navigate("/first-connexion", {
                state: {
                  username: resp.data.userName,
                  email: resp.data.email
                }
              });
              return;
            }

            if (resp.data.active) {
              sessionStorage.setItem("user", JSON.stringify(resp.data));
              // OTP requis pour CETTE connexion : on se fie UNIQUEMENT à `otpPending` (le backend est
              // autoritaire : il tient compte de l'OTP organisme, du challenge appareil ET de l'exemption
              // OTP + appareil de confiance). L'ancien repli « || organisme.otpConnexion === EMAIL »
              // forçait l'écran OTP à tort pour un utilisateur EXEMPT sur un appareil déjà approuvé.
              if (resp.data?.otpPending) {
                toast.success("Veuillez valider la seconde étape de connexion!");
                // postLoginRedirect conservé → consommé après la validation OTP.
                navigate("/confirm-otp");
                window.location.reload();
              } else {
                toast.success("Connexion avec succès!");
                // Redirige vers la destination initiale (deep-link) si présente, sinon l'accueil.
                // window.location.href = navigation COMPLÈTE (bootstrap app + bonne URL).
                const redirect = sessionStorage.getItem("postLoginRedirect") || "/";
                sessionStorage.removeItem("postLoginRedirect");
                window.location.href = redirect;
              }
            } else {
              toast.error("Votre compte n'est pas actif.");
            }

            setLoading(false);
          })
          .catch((err) => {
            console.error("Erreur lors de la récupération de la session:", err);
            toast.error("Erreur lors de la récupération de la session.");
            setLoading(false);
          });
      })
      .catch((err) => {
        console.error("Erreur lors de la tentative de connexion:", err);

        if (err.response) {
          const { status } = err.response;

          if (status === 409) {
            // Session déjà active : proposer de la fermer et de se connecter ici.
            setLoading(false);
            let info = {};
            try {
              const d = err.response.data;
              info = typeof d === "string" ? JSON.parse(d) : (d || {});
            } catch (_) { info = {}; }
            const last = info?.lastConnexion
              ? String(info.lastConnexion).replace("T", " ").slice(0, 19)
              : null;
            Modal.confirm({
              title: "Une session est déjà active",
              content: `Une session est déjà ouverte pour ce compte${last ? ` (dernière activité : ${last})` : ""}. Voulez-vous la fermer et vous connecter ici ?`,
              okText: "Se connecter ici",
              okType: "danger",
              cancelText: "Annuler",
              onOk: () => submitLogin({ ...user, forceLogout: true }),
            });
            return;
          }

          // Utilisateur exempt d'OTP sur un appareil non reconnu : connexion à valider par un admin.
          let respData = {};
          try {
            const d = err.response.data;
            respData = typeof d === "string" ? JSON.parse(d) : (d || {});
          } catch (_) { respData = {}; }
          if (status === 403 && respData?.code === "DEVICE_APPROVAL_REQUIRED") {
            setLoading(false);
            Modal.info({
              title: "Validation administrateur requise",
              content: respData?.message
                || "Votre connexion depuis cet appareil doit être validée par un administrateur. Contactez-le, puis réessayez.",
              okText: "Compris",
            });
            return;
          }

          if (status === 423) {
            toast.error("Votre compte est verrouillé après 3 tentatives échouées.");
          } else if (status === 400 || status === 401) {
            toast.error("Identifiants incorrects. Veuillez vérifier votre nom d'utilisateur et mot de passe.");
          } else {
            toast.error("Une erreur est survenue lors de la connexion. Veuillez réessayer.");
          }
        } else {
          toast.error("Une erreur réseau est survenue. Veuillez vérifier votre connexion.");
        }

        setLoading(false);
      });
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: 'url(assets/img/landing.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(36, 33, 102, 0.12)',
        backdropFilter: 'blur(10px)',
        zIndex: 0
      }} />

      <ToastContainer position="top-center" />

      {/* Main content area */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        position: 'relative',
        zIndex: 1
      }}>
        <div style={{
          width: '100%',
          maxWidth: '1400px',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: '60px',
          alignItems: 'center'
        }}>
          {/* Left Section - PoolIns */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center'
          }}>
            <img
              src={poolins}
              alt="PoolIns"
              style={{
                maxWidth: '200px',
                width: '100%',
                height: 'auto',
                borderRadius: '16px',
                filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))'
              }}
            />
            <Text style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              display: 'block',
              fontStyle: 'italic',
              letterSpacing: '0.3px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
              textAlign: 'center'
            }}>
              Solution intégrée des Assurances
            </Text>
            {/* <Text style={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              display: 'block',
              marginBottom: '40px',
              fontSize: '16px',
              lineHeight: '1.6'
            }}>
              Plateforme de gestion des activités du Pool TPVM-VT GIE
            </Text> */}
          </div>

          {/* Center Section - Login Form */}
          <Card style={{
            width: '450px',
            background: 'white',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            border: 'none',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #242166 0%, #3b82f6 100%)',
              padding: '36px 32px',
              marginTop: '-24px',
              marginLeft: '-24px',
              marginRight: '-24px',
              marginBottom: '32px'
            }}>
              <Title level={2} style={{
                color: 'white',
                margin: 0,
                fontSize: '30px',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                Connexion
              </Title>
              <Text style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '15px',
                display: 'block',
                textAlign: 'center',
                marginTop: '8px'
              }}>
                Accédez à votre espace sécurisé
              </Text>
            </div>

            <div style={{ padding: '0 8px 8px' }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
                requiredMark={false}
              >
                <Form.Item
                  label={<span style={{ color: '#242166', fontWeight: '600', fontSize: '14px' }}>Adresse email</span>}
                  name="userName"
                  rules={[
                    { required: true, message: 'Veuillez entrer votre email' },
                    { type: 'email', message: 'Email invalide' }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#242166' }} />}
                    placeholder="exemple@email.com"
                    size="large"
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '15px'
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label={<span style={{ color: '#242166', fontWeight: '600', fontSize: '14px' }}>Mot de passe</span>}
                  name="password"
                  rules={[{ required: true, message: 'Veuillez entrer votre mot de passe' }]}
                  style={{ marginBottom: '24px' }}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#242166' }} />}
                    placeholder="••••••••"
                    size="large"
                    style={{
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      padding: '12px 16px',
                      fontSize: '15px'
                    }}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: '16px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<LoginOutlined />}
                    size="large"
                    block
                    style={{
                      background: 'linear-gradient(135deg, #242166 0%, #3b82f6 100%)',
                      borderRadius: '12px',
                      height: '50px',
                      fontSize: '16px',
                      fontWeight: '600',
                      border: 'none',
                      boxShadow: '0 4px 12px rgba(36, 33, 102, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {loading ? 'Connexion en cours...' : 'Se connecter'}
                  </Button>
                </Form.Item>

                <div style={{
                  textAlign: 'center',
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  marginTop: '24px'
                }}>
                  <Text style={{ color: '#64748b', fontSize: '13px' }}>
                    Protégé par un cryptage de niveau bancaire
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Text style={{ color: '#242166', fontSize: '12px', fontWeight: '600' }}>
                      🔒 Connexion sécurisée SSL/TLS
                    </Text>
                  </div>
                </div>
              </Form>
            </div>
          </Card>

          {/* Right Section - Pool TPVM-VT GIE Info */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            alignItems: 'center'
          }}>
            {/* Pool Logo */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <img
                src={entete}
                alt="Pool TPVM-VT GIE"
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  height: 'auto'
                }}
              />
            </div>

            {/* Contact Information - directly on background */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
              width: '100%'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <EnvironmentOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  marginTop: '2px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  Rue Gérard DULPHY, Lomégan derrière Radio Pyramide<br />
                  01 B.P. 4866, Lomé - TOGO
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <PhoneOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  +228 92 62 66 66
                </Text>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <WhatsAppOutlined style={{
                  color: '#25D366',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  +228 70 48 11 11
                </Text>
              </div>

              {/* Email */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <MailOutlined style={{
                  color: '#3b82f6',
                  fontSize: '20px',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }} />
                <Text style={{
                  color: 'white',
                  fontSize: '15px',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                  fontWeight: '500'
                }}>
                  poolvtgie@pooltpv.tg
                </Text>
              </div>

              <div style={{
                marginTop: '12px',
                paddingTop: '16px',
                borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '14px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    fontWeight: '600'
                  }}>
                    NIF:
                  </Text>
                  <Text style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    1000 16 1028
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontSize: '14px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                    fontWeight: '600'
                  }}>
                    RCC:
                  </Text>
                  <Text style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    2009 C 0771
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Company Logos Marquee */}
      <div style={{
        padding: '32px 0',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden'
      }}>
        {/* Marquee container */}
        <div style={{
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <style>
            {`
              @keyframes marquee {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              
              .marquee-content {
                display: flex;
                gap: 40px;
                animation: marquee 30s linear infinite;
                will-change: transform;
              }
              
              .marquee-content:hover {
                animation-play-state: paused;
              }
              
              .company-logo-card {
                background: white;
                border-radius: 12px;
                padding: 20px;
                min-width: 160px;
                height: 100px;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                transition: transform 0.3s ease, box-shadow 0.3s ease;
                flex-shrink: 0;
              }
              
              .company-logo-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
              }
            `}
          </style>

          <div className="marquee-content">
            {/* First set of logos */}
            {Array.isArray(insurances) && insurances.map((ins, i) => (
              <div key={`logo-1-${i}`} className="company-logo-card">
                <img
                  src={`data:image/png;base64,${ins.logoDisplay}`}
                  alt={ins.raisonSociale || `Logo ${i + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            ))}

            {/* Duplicate set for seamless loop */}
            {Array.isArray(insurances) && insurances.map((ins, i) => (
              <div key={`logo-2-${i}`} className="company-logo-card">
                <img
                  src={`data:image/png;base64,${ins.logoDisplay}`}
                  alt={ins.raisonSociale || `Logo ${i + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;